"use client";

import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/design-system/button";
import { sharedAuthClient } from "@/lib/sharedAuthClient";
import { trpc } from "@/lib/trpc-client";
import { BrandChoiceScreen } from "./brands-view/BrandChoiceScreen";
import { CreatorAuthMessage } from "./CreatorAuthFrame";
import { CreatorSignIn } from "./CreatorSignIn";
import { CreatorAuthContext } from "./creatorAuthContext";
import { PortalSkeleton } from "./PortalSkeleton";
import { clearSignInEmail, readSignInEmail } from "./signInEmailHint";
import {
  clearLegacyCreatorHint,
  useLegacyCreatorLink,
} from "./useLegacyCreatorLink";

export function CreatorAuthGate({ children }: { children: ReactNode }) {
  const t = useTranslations("authGate");
  const pathname = usePathname();
  const publicPage = pathname === "/one/unsubscribe";
  const config = trpc.creatorAuth.config.useQuery(undefined, {
    enabled: !publicPage,
    staleTime: 60_000,
    retry: false,
  });
  // This standalone public page must never restore legacy login credentials.
  if (publicPage)
    return (
      <CreatorAuthContext.Provider value={{}}>
        {children}
      </CreatorAuthContext.Provider>
    );
  // React Query reports isError on a failed refetch even though the earlier
  // data is still cached. Keep using it rather than unmounting what's on screen.
  if (config.isError && !config.data)
    return (
      <CreatorAuthMessage
        title={t("unavailable.title")}
        body={t("unavailable.body")}
      >
        <Button
          variant="ink"
          className="w-full"
          onClick={() => void config.refetch()}
        >
          {t("unavailable.retry")}
        </Button>
      </CreatorAuthMessage>
    );
  if (!config.data) return <PortalSkeleton />;
  return (
    <SharedCreatorAccess googleEnabled={config.data.googleEnabled}>
      {children}
    </SharedCreatorAccess>
  );
}

function SharedCreatorAccess({
  children,
  googleEnabled,
}: {
  children: ReactNode;
  googleEnabled: boolean;
}) {
  const t = useTranslations("authGate");
  const session = sharedAuthClient.useSession();
  const legacy = useLegacyCreatorLink();
  const pathname = usePathname();
  const params = useSearchParams();
  const cache = useQueryClient();
  const cacheClear = useRef(Promise.resolve());
  const [cacheIdentity, setCacheIdentity] = useState<string | null>(null);
  const [sessionSettled, setSessionSettled] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  // The address someone just applied with on /join/apply, so signing in to
  // choose brands does not start by asking for it again.
  const [appliedEmail] = useState(readSignInEmail);
  const userId = session.data?.user.id ?? null;
  const me = trpc.creatorAuth.me.useQuery(undefined, {
    enabled: !!userId && cacheIdentity === userId,
    retry: false,
    staleTime: 0,
    refetchInterval: 60_000,
  });

  function clearCreatorCache() {
    // Finish older clears before a newer identity can start fetching data.
    cacheClear.current = cacheClear.current.then(async () => {
      for (const queryKey of [
        getQueryKey(trpc.creatorAuth.me),
        getQueryKey(trpc.creatorPortal),
        getQueryKey(trpc.creatorMessaging),
      ]) {
        await cache.cancelQueries({ queryKey });
        cache.removeQueries({ queryKey });
      }
    });
    return cacheClear.current;
  }
  useEffect(() => {
    let active = true;
    void clearCreatorCache().then(() => {
      if (active) setCacheIdentity(userId);
    });
    return () => {
      active = false;
    };
  }, [userId]);

  // better-auth derives isPending from `data === null`, so a signed-out visitor
  // sees it go true on every background refetch — and it refetches the session
  // on every tab focus. Blocking on those would unmount the sign-in form and
  // throw away a code the creator left the tab to go read.
  // Only a successful check counts as settled. Latching on a failed one would
  // swap the first-load retry screen for sign-in, stranding a visitor who holds
  // a good session behind a transient error.
  useEffect(() => {
    if (!session.isPending && !session.error) setSessionSettled(true);
  }, [session.isPending, session.error]);

  useEffect(() => {
    if (me.data?.creator?.id && me.data.creator.id === legacy.hint?.creatorId)
      clearLegacyCreatorHint();
  }, [me.data?.creator?.id, legacy.hint?.creatorId]);

  async function signOut() {
    setSignOutError(null);
    try {
      const result = await sharedAuthClient.signOut();
      if (result.error) {
        setSignOutError(t("signOutFailed"));
        return;
      }
      await clearCreatorCache();
      setCacheIdentity(null);
      await session.refetch();
    } catch {
      setSignOutError(t("signOutFailed"));
    }
  }
  async function signedIn() {
    clearSignInEmail();
    await clearCreatorCache();
    await session.refetch();
  }

  if (!legacy.ready || (session.isPending && !sessionSettled))
    return <PortalSkeleton />;
  // A refetch that fails must not tear the form down either. Once we know the
  // visitor is signed out, sign-in is a better destination than a dead end —
  // their next attempt surfaces its own error if the backend is really down.
  if (session.error && (!sessionSettled || userId))
    return (
      <CreatorAuthMessage title={t("sessionFailed")}>
        <Button
          variant="ink"
          className="w-full"
          onClick={() => void session.refetch()}
        >
          {t("tryAgain")}
        </Button>
      </CreatorAuthMessage>
    );
  if (userId && cacheIdentity !== userId) return <PortalSkeleton />;
  if (!userId || me.error?.data?.code === "UNAUTHORIZED")
    return (
      <CreatorSignIn
        initialEmail={legacy.hint?.email ?? appliedEmail}
        legacyLink={legacy.wasLegacy}
        googleEnabled={googleEnabled}
        onSignedIn={signedIn}
      />
    );
  if (me.isError)
    return (
      <CreatorAuthMessage
        title={t("accountFailed")}
        body={me.error.message}
        error={signOutError}
      >
        <Button
          variant="ink"
          className="w-full"
          onClick={() => void me.refetch()}
        >
          {t("tryAgain")}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => void signOut()}
        >
          {t("useAnotherAccount")}
        </Button>
      </CreatorAuthMessage>
    );
  if (me.data?.authUserId !== userId) return <PortalSkeleton />;
  const account = me.data;
  if (legacy.hint && account.creator?.id !== legacy.hint.creatorId)
    return (
      <CreatorAuthMessage
        title={t("wrongInvite.title")}
        body={t.rich("wrongInvite.body", {
          signedIn: account.email,
          invited: legacy.hint.email,
          emphasis,
        })}
        error={signOutError}
      >
        <Button variant="ink" className="w-full" onClick={() => void signOut()}>
          {t("wrongInvite.switchAccount")}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => {
            clearLegacyCreatorHint();
            window.location.assign("/one");
          }}
        >
          {t("wrongInvite.continue")}
        </Button>
      </CreatorAuthMessage>
    );
  // Both of Inbox's views, Messages and Updates.
  const inbox = pathname === "/one/inbox";
  const orgSlug = params.get("orgSlug");
  const accessible = account.programs.some(
    (program) => program.slug === orgSlug,
  );
  const chooseProgram =
    !orgSlug &&
    params.get("chooseProgram") === "true" &&
    account.programs.length > 1;
  const needsProgram =
    !inbox &&
    pathname !== "/join/link" &&
    (!account.programs.length || (!!orgSlug && !accessible) || chooseProgram);
  // Signed the agreement but on no brand yet, or a creator whose programs
  // have all ended: the way forward is the brand list, not a dead end.
  // `/join/link` passes through so it can forward to `/one`, which lands here.
  const brandChooser =
    account.canChooseBrands &&
    (!account.creator || (!account.programs.length && !inbox));
  const choosingBrands = brandChooser && pathname !== "/join/link";
  // The first way forward is the filled one; any others are outlined, so a
  // creator in several programs still sees one answer before the rest.
  const destinations = [
    ...account.programs.map((program) => {
      const destination = new URLSearchParams(params.toString());
      destination.delete("chooseProgram");
      destination.delete("token");
      destination.set("orgSlug", program.slug);
      return {
        href: chooseProgram
          ? `${pathname}?${destination}`
          : `/one?orgSlug=${encodeURIComponent(program.slug)}`,
        label: t("openProgram", { program: program.name }),
      };
    }),
    ...(account.creator
      ? [{ href: "/one/inbox", label: t("openInbox") }]
      : account.application
        ? []
        : [{ href: "/join/apply", label: t("apply") }]),
  ];
  return (
    <CreatorAuthContext.Provider value={{ account, signOut }}>
      {choosingBrands ? (
        <BrandChoiceScreen
          inboxHref={account.creator ? "/one/inbox" : null}
          signOutError={signOutError}
          onSignOut={() => void signOut()}
        />
      ) : !brandChooser && (!account.creator || needsProgram) ? (
        <CreatorAuthMessage
          title={
            !account.creator
              ? account.application?.status === "PENDING"
                ? t("applicationPending")
                : t("noProfile.title")
              : chooseProgram
                ? t("chooseProgram.title")
                : t("programUnavailable.title")
          }
          body={
            !account.creator
              ? t.rich("noProfile.body", { email: account.email, emphasis })
              : chooseProgram
                ? t.rich("chooseProgram.body", {
                    email: account.email,
                    emphasis,
                  })
                : t.rich("programUnavailable.body", {
                    email: account.email,
                    emphasis,
                  })
          }
          error={signOutError}
        >
          {destinations.map((destination, index) => (
            <Button
              key={destination.href}
              asChild
              variant={index === 0 ? "ink" : "outline"}
              className="w-full"
            >
              <Link href={destination.href}>{destination.label}</Link>
            </Button>
          ))}
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => void signOut()}
          >
            {t("useAnotherAccount")}
          </Button>
        </CreatorAuthMessage>
      ) : (
        <>
          {children}
          {signOutError && (
            <p
              role="alert"
              className="p-nf-ds-4 text-center text-nf-ds-destructive"
            >
              {signOutError}
            </p>
          )}
        </>
      )}
    </CreatorAuthContext.Provider>
  );
}

/** An address shown inside a sentence, lifted to the foreground role. */
function emphasis(chunks: ReactNode) {
  return <span className="text-nf-ds-fg">{chunks}</span>;
}
